import { DailyEventObjectParticipant } from '@daily-co/daily-js';
import { useDailyEvent } from '@daily-co/daily-react';
import { datadogLogs } from '@datadog/browser-logs';
import { EVENT_NAMES } from '@web-app/context/AnalyticsContext';
import { useBaseVideoCallAnalytics } from '@web-app/hooks/useBaseVideoCallAnalytics';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import { isDialOutParticipant } from '@web-app/utils/daily-client/dial-out-participant';
import { useCallback, useRef } from 'react';

interface ProviderVideoCallAnalyticsProps {
  roomId: string;
  appointmentId?: number;
  clientId?: number;
  providerId?: number;
}

/**
 * Hook for tracking provider video call analytics events.
 *
 * Events tracked:
 * - PARTICIPANT_JOINED_CALL: When provider joins the call (participantType: 'provider')
 * - PROVIDER_ADMITTED_PATIENT: When provider admits a patient from the waiting room
 * - PARTICIPANT_LEFT_CALL: When provider leaves the call (via base hook)
 *
 * Datadog: logs "Provider joined meeting" on joined-meeting (after prejoin Join).
 *
 * @param roomId - The Daily room ID
 * @param appointmentId - Optional Healthie appointment ID
 * @param clientId - Optional Healthie client ID
 * @param providerId - Optional Healthie provider ID
 */
export const useProviderVideoCallAnalytics = ({
  roomId,
  appointmentId,
  clientId,
  providerId,
}: ProviderVideoCallAnalyticsProps) => {
  // Use base hook for shared functionality (left-meeting, beforeunload)
  const { getEventProperties, trackEvent } = useBaseVideoCallAnalytics({
    roomId,
    appointmentId,
    clientId,
    providerId,
    participantType: 'provider',
  });

  // Track if we've already fired the join event to prevent duplicates
  const hasTrackedJoin = useRef(false);
  // Track which participants we've already tracked admission for (by session_id)
  const admittedParticipants = useRef(new Set<string>());

  // Track when provider joins the meeting
  // Providers join directly without waiting room
  // Daily.co docs: https://docs.daily.co/reference/daily-js/events/meeting-state#joined-meeting
  useDailyEvent(
    'joined-meeting',
    useCallback(() => {
      // Prevent duplicate tracking if the event fires multiple times
      if (hasTrackedJoin.current) return;
      hasTrackedJoin.current = true;

      trackEvent(EVENT_NAMES.PARTICIPANT_JOINED_CALL, {
        ...getEventProperties(),
        participantType: 'provider',
      });

      datadogLogs.logger.info(
        'Provider joined meeting',
        callLogContext('call-join', {
          roomId,
          participantType: 'provider',
          healthieAppointmentId: appointmentId,
          clientId,
          providerId,
        }),
      );
    }, [trackEvent, getEventProperties, roomId, appointmentId, clientId, providerId]),
  );

  // Track when provider admits a patient (PROVIDER_ADMITTED_PATIENT event)
  // Listen for participant-joined to detect when a patient is admitted from waiting room
  // Patient identification: local: false (remote) && owner: false (not provider)
  // Daily.co docs: https://docs.daily.co/reference/daily-js/events/participant-events#participant-joined
  useDailyEvent(
    'participant-joined',
    useCallback(
      (event: DailyEventObjectParticipant) => {
        const participant = event?.participant;
        if (!participant) return;

        // Only track if we haven't already tracked this participant's admission
        const sessionId = participant.session_id;
        if (!sessionId || admittedParticipants.current.has(sessionId)) return;

        // Check if this is a patient (not provider/supervisor)
        // Patient: local === false (remote participant) && owner === false (not room owner)
        const isPatient =
          !participant.local &&
          !participant.owner &&
          !isDialOutParticipant(participant);

        if (isPatient) {
          admittedParticipants.current.add(sessionId);

          trackEvent(EVENT_NAMES.PROVIDER_ADMITTED_PATIENT, {
            ...getEventProperties(),
            participantId: sessionId,
          });
        }
      },
      [trackEvent, getEventProperties],
    ),
  );
};
